'use client';

import { useState, useEffect, useCallback } from 'react';
import { FolderOpen, Search, X, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Link } from '@/i18n/routing';
import { useLocale, useTranslations } from 'next-intl';
import { useDebounce } from '@/hooks/use-debounce';
import { productPath } from '@/lib/localized-paths';
import { formatPriceForLocale } from '@/lib/pricing';

interface SearchResult {
  id: string;
  type?: 'product';
  name: string;
  slug: string;
  description: string | null;
  category: string;
  basePrice: number;
  displayPrice?: number;
  thumbnail: string | null;
}

interface CategoryResult {
  id: string;
  type: 'category';
  name: string;
  slug: string;
  count: number;
  href: string;
}

interface SearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SearchDialog({ open, onOpenChange }: SearchDialogProps) {
  const t = useTranslations('search');
  const locale = useLocale();
  const [query, setQuery] = useState('');
  const [categories, setCategories] = useState<CategoryResult[]>([]);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const debouncedQuery = useDebounce(query, 300);
  const fallbackCopy = {
    nl: {
      title: 'Zoeken',
      description: 'Zoek producten in de Printen.eu catalogus.',
      categories: 'Categorieën',
      products: 'Producten',
      productCount: 'producten',
      from: 'vanaf',
      showing: 'Weergeven',
      result: 'resultaat',
      results: 'resultaten',
      toClose: 'om te sluiten',
    },
    de: {
      title: 'Suchen',
      description: 'Produkte im Printen.eu-Katalog suchen.',
      categories: 'Kategorien',
      products: 'Produkte',
      productCount: 'Produkte',
      from: 'ab',
      showing: 'Angezeigt',
      result: 'Ergebnis',
      results: 'Ergebnisse',
      toClose: 'zum Schließen',
    },
    en: {
      title: 'Search',
      description: 'Search products in the Printen.eu catalogue.',
      categories: 'Categories',
      products: 'Products',
      productCount: 'products',
      from: 'from',
      showing: 'Showing',
      result: 'result',
      results: 'results',
      toClose: 'to close',
    },
    fr: {
      title: 'Recherche',
      description: 'Rechercher des produits dans le catalogue Printen.eu.',
      categories: 'Catégories',
      products: 'Produits',
      productCount: 'produits',
      from: 'dès',
      showing: 'Affichage',
      result: 'résultat',
      results: 'résultats',
      toClose: 'pour fermer',
    },
  };
  const copy = fallbackCopy[locale as keyof typeof fallbackCopy] || fallbackCopy.nl;

  const searchProducts = useCallback(async (searchQuery: string) => {
    if (!searchQuery || searchQuery.trim().length < 2) {
      setCategories([]);
      setResults([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`/api/search?q=${encodeURIComponent(searchQuery)}&locale=${encodeURIComponent(locale)}`);
      const data = await response.json();
      setCategories(data.categories || []);
      setResults(data.results || []);
    } catch (error) {
      console.error('Search error:', error);
      setCategories([]);
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, [locale]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- bekende linter-bug: setState valt hier ná een await/.then, dus niet synchroon (facebook/react#34905, nog open)
    searchProducts(debouncedQuery);
  }, [debouncedQuery, searchProducts]);

  // Reset query when dialog closes
  useEffect(() => {
    if (!open) {
      setQuery('');
      setCategories([]);
      setResults([]);
    }
  }, [open]);

  const handleResultClick = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 gap-0">
        <DialogTitle className="sr-only">{copy.title}</DialogTitle>
        <DialogDescription className="sr-only">
          {copy.description}
        </DialogDescription>
        {/* Search Input */}
        <div className="flex items-center border-b px-4 py-3">
          <Search className="h-5 w-5 text-gray-400 mr-3" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('placeholder') || 'Zoek producten...'}
            className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-base"
            autoFocus
          />
          {isLoading && <Loader2 className="h-4 w-4 animate-spin text-gray-400 ml-2" />}
        </div>

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto">
          {query.trim().length > 0 && query.trim().length < 2 && (
            <div className="p-8 text-center text-gray-500 text-sm">
              {t('minChars') || 'Typ minimaal 2 tekens om te zoeken'}
            </div>
          )}

          {query.trim().length >= 2 && categories.length === 0 && results.length === 0 && !isLoading && (
            <div className="p-8 text-center text-gray-500">
              <p className="text-base font-medium">{t('noResults') || 'Geen resultaten gevonden'}</p>
              <p className="text-sm mt-1">{t('tryDifferent') || 'Probeer een andere zoekterm'}</p>
            </div>
          )}

          {(categories.length > 0 || results.length > 0) && (
            <div className="p-2 space-y-1">
              {categories.length > 0 && (
                <div className="pb-2">
                  <div className="px-3 pb-1 pt-2 text-xs font-bold uppercase tracking-wide text-gray-400">
                    {copy.categories}
                  </div>
                  {categories.map((category) => (
                    <Link
                      key={category.id}
                      href={category.href}
                      onClick={handleResultClick}
                      className="flex items-center rounded-lg p-3 transition hover:bg-blue-50 group"
                    >
                      <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600 group-hover:bg-blue-100">
                        <FolderOpen className="h-5 w-5" />
                      </div>
                      <div className="ml-4 min-w-0 flex-1">
                        <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition">
                          {category.name}
                        </h3>
                        <p className="mt-0.5 text-sm text-gray-500">
                          {category.count} {copy.productCount}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
              {results.length > 0 && categories.length > 0 && (
                <div className="px-3 pb-1 pt-2 text-xs font-bold uppercase tracking-wide text-gray-400">
                  {copy.products}
                </div>
              )}
              {results.map((product) => (
                <Link
                  key={product.id}
                  href={productPath(locale, product.slug)}
                  onClick={handleResultClick}
                  className="flex items-start p-3 rounded-lg hover:bg-blue-50 transition group"
                >
                  {/* Thumbnail */}
                  <div className="flex-shrink-0 w-16 h-16 bg-gray-100 rounded-lg overflow-hidden">
                    {product.thumbnail ? (
                      <img
                        src={product.thumbnail}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <Search className="h-6 w-6" />
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 ml-4 min-w-0">
                    <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition">
                      {product.name}
                    </h3>
                    {product.description && (
                      <p className="text-sm text-gray-600 mt-0.5 line-clamp-1">
                        {product.description}
                      </p>
                    )}
                    <div className="flex items-center mt-1 space-x-2">
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                        {product.category}
                      </span>
                      <span className="text-sm font-semibold text-blue-600">
                        {copy.from} {formatPriceForLocale(product.displayPrice ?? product.basePrice, locale)}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {query.trim().length === 0 && (
            <div className="p-8 text-center text-gray-400 text-sm">
              <Search className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>{t('startTyping') || 'Begin met typen om te zoeken'}</p>
            </div>
          )}
        </div>

        {/* Footer hint */}
        {(categories.length > 0 || results.length > 0) && (
          <div className="border-t px-4 py-2 text-xs text-gray-500 flex items-center justify-between">
            <span>{t('showing') || copy.showing}: {categories.length + results.length} {categories.length + results.length === 1 ? t('result') || copy.result : t('results') || copy.results}</span>
            <span className="text-gray-400">ESC {t('toClose') || copy.toClose}</span>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
